import { pickString } from "./staffDailyHours";

/** "09:30" or "9:30" → minutes from midnight; invalid → null */
export function parseClockToMinutes(str) {
  if (str == null || str === "") return null;
  const s = String(str).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 47 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/**
 * Subtract break time from attributed work ms.
 * @param {number} attributedMs
 * @param {number} sessionCount
 * @param {{ breakMinutesPerDay?: number, breakMinutesPerSession?: number }} options
 */
export function applyBreakDeduction(attributedMs, sessionCount, options) {
  const breakMinutesPerDay = Math.max(0, Math.floor(Number(options?.breakMinutesPerDay) || 0));
  const breakMinutesPerSession = Math.max(0, Math.floor(Number(options?.breakMinutesPerSession) || 0));
  const bd = breakMinutesPerDay * 60000;
  const bs = breakMinutesPerSession * Math.max(0, sessionCount) * 60000;
  return Math.max(0, attributedMs - bd - bs);
}

/** Build expected shift window on calendar day (handles end before start as next day). */
export function expectedWindowFromDay(startOfDay, startMin, endMin) {
  if (startMin == null || endMin == null) return null;
  const d0 = new Date(startOfDay);
  const y = d0.getFullYear();
  const mo = d0.getMonth();
  const da = d0.getDate();
  const s = new Date(y, mo, da, Math.floor(startMin / 60), startMin % 60, 0, 0);
  let e = new Date(y, mo, da, Math.floor(endMin / 60), endMin % 60, 0, 0);
  if (e.getTime() <= s.getTime()) e = new Date(e.getTime() + 24 * 3600000);
  return { expStart: s, expEnd: e };
}

export function actualFirstLast(sessions) {
  if (!sessions?.length) return { first: null, last: null };
  let first = sessions[0].start;
  let last = sessions[0].end;
  for (const s of sessions) {
    if (s.start < first) first = s.start;
    if (s.end > last) last = s.end;
  }
  return { first, last };
}

const SHIFT_START_KEYS = [
  "ExpectedShiftStart",
  "expectedShiftStart",
  "shiftStart",
  "ShiftStart",
  "rosterStart",
  "RosterStart",
  "expectedStart",
];
const SHIFT_END_KEYS = [
  "ExpectedShiftEnd",
  "expectedShiftEnd",
  "shiftEnd",
  "ShiftEnd",
  "rosterEnd",
  "RosterEnd",
  "expectedEnd",
];

/**
 * @param {string} empId
 * @param {object|null} meta — employeeMeta[empId]
 * @param {{ defaultShiftStart: string, defaultShiftEnd: string }} defaults
 * @param {Record<string, { start?: string, end?: string }|undefined>} overridesByEmp
 */
export function resolveExpectedShiftStrings(empId, meta, defaults, overridesByEmp) {
  const o = overridesByEmp?.[empId];
  const raw = meta?.raw || {};
  const startStr =
    (o?.start && String(o.start).trim()) ||
    pickString(raw, SHIFT_START_KEYS) ||
    defaults.defaultShiftStart ||
    "";
  const endStr =
    (o?.end && String(o.end).trim()) ||
    pickString(raw, SHIFT_END_KEYS) ||
    defaults.defaultShiftEnd ||
    "";
  return {
    startStr: String(startStr).trim(),
    endStr: String(endStr).trim(),
    startMin: parseClockToMinutes(startStr),
    endMin: parseClockToMinutes(endStr),
  };
}

export function formatClock(d) {
  if (!d) return "—";
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
}

/** Monday 00:00 of the week containing `d` (local). */
export function startOfWeekMonday(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  return x;
}

export function ymdKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Compare actual first/last punch to expected shift window.
 * @returns {{ parts: string[], lateMs: number, earlyMs: number }}
 */
export function compareRosterToActual(rosterWindow, first, last) {
  if (!rosterWindow?.expStart || !first || !last) {
    return { parts: [], lateMs: 0, earlyMs: 0 };
  }
  const lateMs = first.getTime() > rosterWindow.expStart.getTime() ? first.getTime() - rosterWindow.expStart.getTime() : 0;
  const earlyMs = last.getTime() < rosterWindow.expEnd.getTime() ? rosterWindow.expEnd.getTime() - last.getTime() : 0;
  const parts = [];
  if (lateMs > 60000) parts.push(`Late in ${Math.round(lateMs / 60000)}m`);
  if (earlyMs > 60000) parts.push(`Early out ${Math.round(earlyMs / 60000)}m`);
  return { lateMs, earlyMs, parts };
}
